// Role / persona PRESENTATION constants for the Organization pages.
// Pure display metadata keyed by the four real tenant roles (admin,
// ops_manager, trader, executive) plus the derived platform_admin. No data —
// member counts, tenants, users all come live from tenant-service via
// @/lib/api/tenantClient.

/** The four roles tenant-service accepts for members (ROLE_VALUES). */
export const TENANT_ROLES = ["admin", "ops_manager", "trader", "executive"] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];

export const ROLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "admin", label: "Admin" },
  { value: "ops_manager", label: "Ops Manager" },
  { value: "trader", label: "Trader" },
  { value: "executive", label: "Executive" },
];

// ---------------------------------------------------------------------------
// Personas — display info per role (label, blurb, example titles, accent)
// ---------------------------------------------------------------------------

export interface Persona {
  /** Role id this persona presents (one of the real roles). */
  id: string;
  label: string;
  blurb: string;
  /** Console apps this persona lives in (ids match CONSOLE_APPS). */
  apps: string[];
  /** Example job titles within the persona. */
  titles: string[];
  accent: string;
}

export const PERSONAS: Persona[] = [
  {
    id: "executive",
    label: "Executives",
    blurb: "Oversee the whole operation. Live in the Overview; read-only across products.",
    apps: ["overview", "trade", "asset"],
    titles: ["CEO", "CFO", "COO", "Head of Storage"],
    accent: "#2dd4bf",
  },
  {
    id: "trader",
    label: "Power Traders",
    blurb: "The trading desk — forecast, optimise and dispatch on IEX while staying DSM-compliant.",
    apps: ["trade", "comply", "data"],
    titles: ["Trading Analyst", "Quant", "Desk Manager"],
    accent: "#60a5fa",
  },
  {
    id: "ops_manager",
    label: "Operators & Engineers",
    blurb: "Run the plant — control-room analytics, hardware health, telemetry and maintenance intelligence.",
    apps: ["asset", "health", "data"],
    titles: ["Plant Operator", "BESS Engineer", "O&M Lead"],
    accent: "#34d399",
  },
  {
    id: "admin",
    label: "Administrators",
    blurb: "Own the workspace — members, roles and platform governance.",
    apps: ["manage"],
    titles: ["Workspace Owner", "IT / Security Admin"],
    accent: "#f59e0b",
  },
];

/** Map any role (incl. platform_admin) to its presentation persona. */
export function personaForRole(role: string): Persona {
  const id = role === "platform_admin" ? "admin" : role;
  return PERSONAS.find((p) => p.id === id) ?? PERSONAS[3]!;
}

// ---------------------------------------------------------------------------
// App-access matrix — role × console app (drives the Roles & Access page).
// Informational only; enforcement is server-side (JWT claims + Postgres RLS).
// ---------------------------------------------------------------------------

export interface AppCol {
  id: string;
  label: string;
}

export const APP_COLS: AppCol[] = [
  { id: "overview", label: "Overview" },
  { id: "trade", label: "Trade" },
  { id: "comply", label: "Compliance" },
  { id: "asset", label: "Asset" },
  { id: "health", label: "Health" },
  { id: "data", label: "Data" },
  { id: "manage", label: "Manage" },
];

export interface RoleAccess {
  role: string;
  label: string;
  persona: string;
  /** appId → access level. */
  access: Record<string, "full" | "view" | "none">;
}

export const APP_ACCESS: RoleAccess[] = [
  {
    role: "executive",
    label: "Executive",
    persona: "Executives",
    access: { overview: "full", trade: "view", comply: "view", asset: "view", health: "view", data: "view", manage: "view" },
  },
  {
    role: "trader",
    label: "Trader",
    persona: "Power Traders",
    access: { overview: "view", trade: "full", comply: "full", asset: "view", health: "view", data: "full", manage: "none" },
  },
  {
    role: "ops_manager",
    label: "Ops Manager",
    persona: "Operators & Engineers",
    access: { overview: "view", trade: "none", comply: "view", asset: "full", health: "full", data: "full", manage: "none" },
  },
  {
    role: "admin",
    label: "Admin",
    persona: "Administrators",
    access: { overview: "full", trade: "full", comply: "full", asset: "full", health: "full", data: "full", manage: "full" },
  },
  {
    role: "platform_admin",
    label: "Platform Admin",
    persona: "Administrators",
    access: { overview: "full", trade: "full", comply: "full", asset: "full", health: "full", data: "full", manage: "full" },
  },
];
